// =============================================================================
// MobileInventory.tsx
// Mobile Inventory tab — Items / History toggle, search, stat cards, and the
// item / history rows. Tapping a row opens MobileInventorySheet (mounted
// permanently by MobileLayout, toggled via setSelectedItem).
// =============================================================================

import React, { useMemo, useState } from 'react';

import { EmptyState } from '../ui';

import { formatDate } from '../../lib/utils';

import { S, ACTION_TONE, ACTION_LABEL } from './mobileStyles';
import type { DateRange } from './sheets/DateRangePicker';
import { labelForRange } from './sheets/DateRangePicker';

// ── TYPES ────────────────────────────────────────────────────────────

interface MobileInventoryProps {
  inventory: any[];
  filteredInventory: any[];
  invLog: any[];
  invView: 'items' | 'history';
  setInvView: (v: 'items' | 'history') => void;
  invSearch: string;
  setInvSearch: (v: string) => void;
  lowThreshold: number;
  locale: string;
  isCollapsed: boolean;
  setSelectedItem: (item: any) => void;
  // Item-filter wired from MobileInventorySheet's "View all" link. When
  // present, the history tab renders only entries for this item and shows a
  // clear chip.
  historyItemId?: string | null;
  historyItemName?: string;
  clearHistoryItem?: () => void;
  // Date range — applies to History only (Items is "right now" stock state).
  dateRange: DateRange;
  onOpenDateRange: () => void;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

const HIST_ACTION_FILTERS = ['all', 'ADD', 'ISSUE', 'RETURN'] as const;
const HIST_ACTION_LABEL: Record<string, string> = {
  all:    'All',
  ADD:    'Received',
  ISSUE:  'Issued',
  RETURN: 'Returned',
};

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileInventory({
  inventory, filteredInventory, invLog, invView, setInvView,
  invSearch, setInvSearch, lowThreshold, locale, isCollapsed,
  setSelectedItem,
  historyItemId, historyItemName, clearHistoryItem,
  dateRange, onOpenDateRange,
}: MobileInventoryProps) {

  const dateActive = dateRange.value !== 'all' && (dateRange.from !== '' || dateRange.to !== '');

  // ── derived ──
  const lowStockCount = inventory.filter(i => i.available <= lowThreshold).length;
  const totalIssued   = inventory.reduce((s: number, i: any) => s + (i.issued || 0), 0);

  // ── state ──
  const [histAction, setHistAction] = useState<typeof HIST_ACTION_FILTERS[number]>('all');

  // History rows respect both the action filter and the optional item-filter
  // chip set from the detail sheet's "View all" link.
  const filteredLog = useMemo(() => {
    let rows = invLog;
    if (historyItemId) rows = rows.filter((e: any) => e.item_id === historyItemId);
    if (histAction !== 'all') rows = rows.filter((e: any) => e.action === histAction);
    return rows;
  }, [invLog, historyItemId, histAction]);

  return (
    <div className="m-tab-content">

      {/* Stat cards — mirror the desktop pattern by using .sc / .sl / .sn /
          tone-modifier classes. .m-sc applies the mobile padding/font tweaks. */}
      {!isCollapsed && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 8 }}>
          <div className="sc cg m-sc" style={{ textAlign: 'center' }}>
            <div className="sn" style={{ color: 'var(--accent)' }}>{inventory.length}</div>
            <div className="sl">SKUs</div>
          </div>
          <div className="sc cb m-sc" style={{ textAlign: 'center' }}>
            <div className="sn" style={{ color: 'var(--blue)' }}>{totalIssued}</div>
            <div className="sl">Issued</div>
          </div>
          <div className="sc cn m-sc" style={{ textAlign: 'center' }}>
            <div className="sn" style={{ color: lowStockCount > 0 ? 'var(--red)' : 'var(--accent)' }}>{lowStockCount}</div>
            <div className="sl">Low stock</div>
          </div>
        </div>
      )}

      {/* Toggle + Search */}
      {!isCollapsed && (
        <div style={{ ...S.card, padding: '8px 10px', marginBottom: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Items / History segmented toggle */}
            <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }}>
              <button onClick={() => setInvView('items')}   style={{ padding: '6px 16px', fontSize: 'var(--m-text-button)', fontWeight: 600, borderRadius: 6, border: 'none', background: invView === 'items'   ? 'var(--accent)' : 'transparent', color: invView === 'items'   ? 'white' : 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease' }}>Items</button>
              <button onClick={() => setInvView('history')} style={{ padding: '6px 16px', fontSize: 'var(--m-text-button)', fontWeight: 600, borderRadius: 6, border: 'none', background: invView === 'history' ? 'var(--accent)' : 'transparent', color: invView === 'history' ? 'white' : 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s ease' }}>History</button>
            </div>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>
              {invView === 'items' ? `${filteredInventory.length} items` : `${filteredLog.length} records`}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <svg viewBox="0 0 24 24" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, fill: 'none', stroke: 'var(--text3)', strokeWidth: 1.5 }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input
                value={invSearch}
                onChange={e => setInvSearch(e.target.value)}
                placeholder={invView === 'items' ? 'Search items…' : 'Search history…'}
                className="input"
                style={{ padding: '8px 10px 8px 28px' }}
              />
            </div>
            {/* History-only date range button — Items view is "right now"
                stock state and a date range doesn't apply meaningfully. */}
            {invView === 'history' && (
              <button
                onClick={onOpenDateRange}
                className={`filter-chip ${dateActive ? 'is-active' : ''}`}
                aria-label={dateActive ? `Date range: ${labelForRange(dateRange)}` : 'Pick date range'}
                title={labelForRange(dateRange)}
                style={{ padding: '6px 10px', flexShrink: 0 }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
              </button>
            )}
            {/* Mobile add-item button intentionally omitted — items land in
                inventory automatically when a Delivered order is stocked. A
                dedicated Add Item sheet is tracked in TODO. */}
          </div>
        </div>
      )}

      {/* Item filter chip (from sheet's "View all" link) */}
      {invView === 'history' && historyItemId && !isCollapsed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, padding: '0 2px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', background: 'var(--accent-bg)',
            color: 'var(--accent)', fontSize: 'var(--m-text-label)', fontWeight: 600,
            borderRadius: 999,
          }}>
            <span>Filtered: {historyItemName}</span>
            <button
              onClick={() => clearHistoryItem && clearHistoryItem()}
              aria-label="Clear filter"
              style={{
                width: 18, height: 18, borderRadius: '50%', border: 'none',
                background: 'var(--surface2)', color: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0, fontFamily: 'inherit',
              }}
            >
              <svg viewBox="0 0 12 12" style={{ width: 8, height: 8, fill: 'none', stroke: 'currentColor', strokeWidth: 1.8 }}>
                <path d="M3 3l6 6M9 3l-6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* History action filter chips */}
      {invView === 'history' && !isCollapsed && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 8, paddingLeft: 2 }}>
          {HIST_ACTION_FILTERS.map(a => (
            <button
              key={a}
              onClick={() => setHistAction(a)}
              className={`filter-chip ${histAction === a ? 'is-active' : ''}`}
              style={{ padding: '6px 14px', fontSize: 'var(--m-text-button)', fontWeight: 600 }}
            >{HIST_ACTION_LABEL[a]}</button>
          ))}
        </div>
      )}

      {/* Items view */}
      {invView === 'items' && (
        <div style={S.card}>
          {filteredInventory.length === 0 ? (
            <EmptyState
              title="No inventory yet"
              description="Items appear here automatically when a delivered order is stocked."
            />
          ) : filteredInventory.map((item: any) => {
            const isLow = item.available <= lowThreshold;
            const isOut = item.available === 0;
            return (
              <div
                key={item.id}
                onClick={() => setSelectedItem(item)}
                className="m-row"
                style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 500 }}>{item.name}</div>
                  <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)', marginTop: 1 }}>
                    Available: <strong style={{ color: isOut || isLow ? 'var(--red)' : 'var(--accent)' }}>{item.available}</strong>
                    {item.size ? ` · ${item.size}` : ''}
                    {item.code ? ` · ${item.code}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={`pill-u ${isOut || isLow ? 'red' : 'green'}`}>
                    {isOut ? 'Out' : isLow ? 'Low' : 'OK'}
                  </span>
                  <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, fill: 'none', stroke: 'var(--text3)', strokeWidth: 1.5, flexShrink: 0 }}>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History view */}
      {invView === 'history' && (
        <div style={S.card}>
          {filteredLog.length === 0 ? (
            <EmptyState
              title="No transactions yet"
              description={historyItemId ? 'No history for this item.' : 'Issue or return items to see them here.'}
            />
          ) : filteredLog.slice(0, 50).map((entry: any) => {
            const tone = ACTION_TONE[entry.action] || 'green';
            const label = ACTION_LABEL[entry.action] || entry.action;
            return (
              <div key={entry.id} style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '0.5px solid var(--border)', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 500 }}>{entry.item_name}</div>
                  <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)' }}>
                    {formatDate(entry.timestamp, locale)}{entry.notes ? ` · ${entry.notes}` : ''}
                  </div>
                </div>
                <span className={`pill-u ${tone}`}>{label}</span>
                <span style={{ fontSize: 'var(--m-text-body)', fontWeight: 600, color: entry.action === 'ISSUE' ? 'var(--red)' : 'var(--accent)', minWidth: 30, textAlign: 'right' }}>
                  {entry.action === 'ISSUE' ? '−' : '+'}{entry.quantity}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
